import { useState } from "react";
import { toast } from "sonner";
import { Loader2, FileDown, ShieldCheck, BarChart3, Receipt, IndianRupee, FileSpreadsheet } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const REPORTS = [
  { key: "police", file: "Police Verification Report.pdf", path: "police.pdf", icon: ShieldCheck,
    title: "Police Verification Report", desc: "Daily police report — every guest arrival, stayover and departure with ID, pax and timings." },
  { key: "manager", file: "Manager Report.pdf", path: "manager.pdf", icon: BarChart3,
    title: "Manager Report", desc: "Today / PTD / YTD — room charges, taxes, paid outs, payments, room summary and statistics." },
  { key: "expense", file: "Expense Report.pdf", path: "expense.pdf", icon: Receipt,
    title: "Expense Report", desc: "Every expense voucher line with pay-method-wise and charge-wise summaries." },
  { key: "revenue", file: "Detail Revenue Report.pdf", path: "revenue.pdf", icon: IndianRupee,
    title: "Detail Revenue Report", desc: "Per-reservation revenue with source, room rate, total, commission and net revenue." },
  { key: "gst", file: "GST Data.xlsx", path: "gst.xlsx", icon: FileSpreadsheet, excel: true,
    title: "GST Data", desc: "GSTR-ready Excel — B2B (GSTIN invoices) and B2C sheets with taxable values and totals." },
];

export default function ReportsTab({ pid, month: initialMonth, propertyName }) {
  const [month, setMonth] = useState(initialMonth);
  const [busy, setBusy] = useState("");

  const download = async (r) => {
    setBusy(r.key);
    try {
      const res = await api.get(`/pms/properties/${pid}/reports/${r.path}`, { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = r.file; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${r.file} downloaded`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  return (
    <div className="reveal max-w-4xl" data-testid="reports-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h3 className="font-display font-bold text-slate-900">Monthly reports — {propertyName}</h3>
          <p className="text-xs text-slate-500 mt-0.5">Property-wise, one month at a time. PDFs for operations; GST data as Excel.</p>
        </div>
        <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
          Month
          <input data-testid="reports-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" />
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.key} data-testid={`report-card-${r.key}`}
              className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col gap-3 hover:border-indigo-300 transition-colors">
              <div className="flex items-start gap-3">
                <div className={`rounded-xl p-2.5 ${r.excel ? "bg-emerald-50 text-emerald-700" : "bg-indigo-50 text-indigo-700"}`}><Icon size={18} /></div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{r.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{r.desc}</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-auto">
                <span className="text-[11px] font-mono text-slate-400">{r.file}</span>
                <button data-testid={`download-${r.key}-button`} onClick={() => download(r)} disabled={!!busy}
                  className={`rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-2 text-white ${r.excel ? "bg-emerald-600 hover:bg-emerald-700" : "btn-brand"}`}>
                  {busy === r.key ? <Loader2 className="animate-spin" size={13} /> : <FileDown size={13} />}
                  {r.excel ? "Download Excel" : "Download PDF"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
